import { ComponentType } from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';

import { Tendency } from '@cast/shared-types';

import {
  ArrowDownRight,
  ArrowUpRight,
  CaretDown,
  CaretUp,
  Minus,
} from '../../../../components/icons';
import { tokens } from '../../../../theme/generated/tokens';
import { SxProps } from '../../../../types';
import { mergeSx } from '../../../../utils';

import { tendencyIconClassNames } from './classNames';
import { TendencyStableIcon } from './TendencyStableIcon';
import {
  TendencyIconSize,
  tendencyIconSizePxMap,
  TendencyIconVariant,
} from './types';

export type TendencyIconProps = {
  /** Icon glyph style */
  variant: TendencyIconVariant;
  /** Direction of change */
  tendency: Tendency;
  /** Whether an increase is a good thing — flips up/down colors */
  upIsPositive?: boolean;
  /** Icon size (`md` = 12px) */
  size?: TendencyIconSize;
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** `sx` prop */
  sx?: SxProps;
};

type TendencyGlyphProps = {
  className?: string;
};

const CaretUpFilled = (props: TendencyGlyphProps) => (
  <CaretUp weight="fill" {...props} />
);
const CaretDownFilled = (props: TendencyGlyphProps) => (
  <CaretDown weight="fill" {...props} />
);

const arrowMap: Record<Tendency, ComponentType<TendencyGlyphProps>> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  stable: TendencyStableIcon,
};

const caretMap: Record<Tendency, ComponentType<TendencyGlyphProps>> = {
  up: CaretUpFilled,
  down: CaretDownFilled,
  stable: Minus,
};

const tendencyColors: Record<
  'default' | 'upIsPositive',
  Record<Tendency, string>
> = {
  default: {
    up: tokens.theme.fg.status.error,
    down: tokens.theme.fg.status.success,
    stable: tokens.theme.fg.status.neutral,
  },
  upIsPositive: {
    up: tokens.theme.fg.status.success,
    down: tokens.theme.fg.status.error,
    stable: tokens.theme.fg.status.neutral,
  },
};

export const TendencyIcon = ({
  variant,
  tendency,
  upIsPositive = false,
  size = 'md',
  className,
  testId,
  sx,
}: TendencyIconProps) => {
  const Icon = variant === 'arrow' ? arrowMap[tendency] : caretMap[tendency];

  return (
    <Box
      component="span"
      className={clsx(
        tendencyIconClassNames.root,
        tendencyIconClassNames[tendency],
        tendencyIconClassNames[variant],
        tendencyIconClassNames[size],
        upIsPositive && tendencyIconClassNames.upIsPositive,
        className
      )}
      data-testid={testId}
      data-tendency={tendency}
      sx={mergeSx(
        {
          display: 'inline-flex',
          lineHeight: 0,
          fontSize: tendencyIconSizePxMap[size],
          color: upIsPositive
            ? tendencyColors.upIsPositive[tendency]
            : tendencyColors.default[tendency],
        },
        sx
      )}
    >
      <Icon />
    </Box>
  );
};

TendencyIcon.displayName = 'TendencyIcon';
